// components/MetricsOverview.tsx
import React from 'react';
import { Grid, Card, CardContent, Typography, Box } from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Business,
  Cloud,
  CheckCircle,
  Schedule,
} from '@mui/icons-material';

interface SubMetric {
  label: string;
  value: number | string;
  percentage: string;
  icon: React.ReactNode;
}

interface MetricCardData {
  title: string;
  value: number | string;
  change: string;
  isPositive: boolean;
  icon: React.ReactNode;
  color: string;
  subMetrics?: SubMetric[];
}

interface MetricsOverviewProps {
  data: any;
}

export const MetricsOverview: React.FC<MetricsOverviewProps> = ({ data }) => {
  // const data = {
  //   accounts?.aws: {
  //     totalCount: 5,
  //     lastMonth: 0,
  //     thisMonth: 2,
  //     percentageChange: '100.00',
  //   },
  //   users: {
  //     totalCount: 86,
  //     lastMonth: 30,
  //     thisMonth: 8,
  //     percentageChange: '-73.33',
  //     active: {
  //       count: 86,
  //       percentage: '100.00',
  //     },
  //     verified: {
  //       count: 69,
  //       percentage: '80.23',
  //     },
  //   },
  //   companies: {
  //     totalCount: 42,
  //     lastMonth: 25,
  //     thisMonth: 9,
  //     percentageChange: '-64.00',
  //     active: {
  //       count: 2,
  //       percentage: '4.76',
  //     },
  //     trialing: {
  //       count: 39,
  //       percentage: '92.86',
  //     },
  //   },
  // };

  const metrics: MetricCardData[] = [
    {
      title: 'AWS Accounts',
      value: data?.accounts?.aws?.totalCount || '0.00',
      change: data?.accounts?.aws?.percentageChange || '0.00',
      isPositive:
        parseFloat(data?.accounts?.aws?.percentageChange || '0.00') >= 0,
      icon: <Cloud />,
      color: '#3f51b5',
      subMetrics: [
        {
          label: 'This Month',
          value: data?.accounts?.aws?.thisMonth || '0.00',
          percentage: data?.accounts?.aws?.totalCount
            ? (
                ((data.accounts?.aws.thisMonth || 0) /
                  data.accounts?.aws.totalCount) *
                100
              ).toFixed(2)
            : '0.00',
          icon: <TrendingUp />,
        },
        {
          label: 'Last Month',
          value: data?.accounts?.aws?.lastMonth || '0.00',
          percentage:
            data?.accounts?.aws?.lastMonth === 0 ||
            !data?.accounts?.aws?.lastMonth
              ? '0.00'
              : data?.accounts?.aws?.totalCount
              ? (
                  (data.accounts?.aws.lastMonth /
                    data.accounts?.aws.totalCount) *
                  100
                ).toFixed(2)
              : '0.00',
          icon: <Schedule />,
        },
      ],
    },
    // {
    //   title: 'Total Users',
    //   value: data?.users?.totalCount || '0.00',
    //   change: data?.users?.percentageChange || '0.00',
    //   isPositive: parseFloat(data?.users?.percentageChange || '0.00') >= 0,
    //   icon: <People />,
    //   color: '#9c27b0',
    //   subMetrics: [
    //     {
    //       label: 'Monthly Active',
    //       value: data?.users?.thisMonth || '0.00',
    //       percentage: data?.users?.totalCount
    //         ? (
    //             ((data.users.thisMonth || 0) / data.users.totalCount) *
    //             100
    //           ).toFixed(2)
    //         : '0.00',
    //       icon: <Person />,
    //     },
    //     {
    //       label: 'Total Verified',
    //       value: data?.users?.verified?.count || '0.00',
    //       percentage: data?.users?.verified?.percentage || '0.00',
    //       icon: <CheckCircle />,
    //     },
    //   ],
    // },
    {
      title: 'Companies',
      value: data?.companies?.totalCount || '0.00',
      change: data?.companies?.percentageChange || '0.00',
      isPositive: parseFloat(data?.companies?.percentageChange || '0.00') >= 0,
      icon: <Business />,
      color: '#ff9800',
      subMetrics: [
        {
          label: 'Total Active',
          value: data?.companies?.active?.count || '0.00',
          percentage: data?.companies?.active?.percentage || '0.00',
          icon: <CheckCircle />,
        },
        {
          label: 'Total Trialing',
          value: data?.companies?.trialing?.count || '0.00',
          percentage: data?.companies?.trialing?.percentage || '0.00',
          icon: <Schedule />,
        },
      ],
    },
  ];

  return (
    <Grid container spacing={3}>
      {metrics.map((metric, index) => (
        <Grid
          width={'50%'}
          item
          // xs={12}
          // sm={6}
          // lg={4}
          key={index}
        >
          <Card className="metricCard">
            <CardContent>
              <Box className="metricCardHeader">
                <div className="d-flex align-center">
                  <div
                    className="metricIcon"
                    style={{ backgroundColor: metric.color }}
                  >
                    {metric.icon}
                  </div>
                  <div className="ml-10">
                    <Typography variant="body1" className="metricValue">
                      {metric.value}
                    </Typography>
                  </div>
                </div>
                <Box className="changeBadge">
                  <span
                    className={`changeBadgeContent ${
                      metric.isPositive ? 'positive' : 'negative'
                    }`}
                  >
                    {metric.isPositive ? (
                      <TrendingUp className="trendIcon" />
                    ) : (
                      <TrendingDown className="trendIcon" />
                    )}
                    {Math.abs(parseFloat(metric.change))}%
                  </span>
                  <p className="subMetricPercentage">vs last month</p>
                </Box>
              </Box>

              <Box className="metricMainInfo">
                <Typography variant="body2" className="metricTitle">
                  {metric.title}
                </Typography>
              </Box>

              {metric.subMetrics && (
                <Box className="subMetricsContainer">
                  {metric.subMetrics.map((subMetric, idx) => (
                    <Box key={idx} className="subMetricRow">
                      <Box className="subMetricLabel">
                        <span className="subMetricIcon">{subMetric.icon}</span>
                        <span className="subMetricText">{subMetric.label}</span>
                      </Box>
                      <Box className="subMetricValues">
                        <span className="subMetricValue">
                          {subMetric.value}
                        </span>
                        <span className="subMetricPercentage">
                          ({subMetric.percentage}%)
                        </span>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};
